/**
 * Centralized configuration for OncoRisk components
 * Contains all shared data needed by sidebar, content, tables and other components
 */

// Main configuration object for OncoRisk
const oncoRiskConfig = {
  // Sidebar items with their metadata
  items: {
    // Analysis tools
    'pipelineanalysis': {
      label: 'Pipeline Analysis',
      icon: 'file-medical-alt',
      description: 'View and manage pipeline analysis results and processes.',
      group: 'pipelineanalysis'
    },  
    'analysistools': {
      label: 'Network Tools',
      icon: 'microscope',
      description: 'View and manage analysis tools.',
      group: 'analysistools'
    },
    // Query tool
    'query': {
      label: 'Query',
      icon: 'search',
      description: 'Run ad-hoc queries on data.',
      group: 'analysistools'
    },

    // MAF tools
    'mafupload': {
      label: 'MAF Tools',
      icon: 'tools',
      description: 'Upload MAF files for analysis.',
      group: 'analysistools'
    },
    
    // Knowledge Base items
    'assertions': {
      label: 'Assertions',
      icon: 'check-circle',
      description: 'Review and manage clinical assertions and their evidence.',
      group: 'knowledge'
    },
    'evidence': {
      label: 'Evidence',
      icon: 'file-alt',
      description: 'Examine supporting evidence for clinical assertions.',
      group: 'knowledge'
    },
    'molecularprofiles': {
      label: 'Molecular Profiles',
      icon: 'dna',
      description: 'Explore molecular profiles and related genomic data.',
      group: 'knowledge'
    },
    'features': {
      label: 'Features',
      icon: 'puzzle-piece',
      description: 'Analyze biological features and their clinical significance.',
      group: 'knowledge'
    },
    'variants': {
      label: 'Variants',
      icon: 'random',
      description: 'Browse genetic variants and their associated data.',
      group: 'knowledge'
    },
    'diseases': {
      label: 'Diseases',
      icon: 'heartbeat',
      description: 'Explore disease data and associated molecular features.',
      group: 'knowledge'
    },
    'therapies': {
      label: 'Therapies',
      icon: 'pills',
      description: 'Review therapeutic options and treatment information.',
      group: 'knowledge'
    },
    'phenotypes': {
      label: 'Phenotypes',
      icon: 'user-tag',
      description: 'Examine phenotypic manifestations and clinical presentations.',
      group: 'knowledge'
    },
    'sources': {
      label: 'Sources',
      icon: 'database',
      description: 'Manage reference sources and citations.',
      group: 'knowledge'
    },

    // Management items
    'recommendations': {
      label: 'Recommendations',
      icon: 'clipboard-list',
      description: 'View and manage patient care recommendations.',
      group: 'management'
    },
    'treatmentplans': {
      label: 'Treatment Plans',
      icon: 'procedures',
      description: 'Create and manage patient treatment plans.',
      group: 'management'
    },

    // Special items
    'home': {
      label: 'Home',
      icon: 'home',
      description: 'Return to the OncoRisk dashboard.',
      group: 'special'
    },
    'settings': {
      label: 'Settings',
      icon: 'cogs',
      description: 'Configure application preferences and user access.',
      group: 'system'
    },
    'help': {
      label: 'Help',
      icon: 'question-circle',
      description: 'Help and documentation.',
      group: 'special'
    }
  },
  // Sidebar groups configuration
  groups: [
    {
      id: 'main',
      title: 'Analysis Tools',
      items: ['pipelineanalysis', 'analysistools', 'query', 'mafupload']
    },
    {
      id: 'knowledge',
      title: 'Knowledge Base',
      items: [
        'assertions',
        'evidence',
        'molecularprofiles',
        'features',
        'variants',
        'diseases',
        'therapies',
        'phenotypes',
        'sources'
      ]
    },
    {
      id: 'system',
      title: 'System',
      items: ['settings', 'help']
    },

  ],

  // API endpoints
  api: {
    baseUrl: 'https://service8.szapfs.org',
    endpoints: {
      browseTable: '/browse_table',
      search: '/oncology_search',
      fetchAnalysisJob: '/fetch_analysis_job',
      webserverCookie: '/webserver_cookie',
      uploadChunk: '/upload_chunk',
      finalizeUpload: '/finalize_upload'

    }
  },

  // UI configuration
  ui: {
    theme: {
      primary: 'rgba(255, 255, 255, 0.85)', // Elegant white instead of blue
      secondary: 'rgba(30, 36, 44, 0.8)',
      background: '#161a1e',
      text: '#f2f5fa',
      textSecondary: 'rgba(255, 255, 255, 0.6)'
    },
    pagination: {
      defaultPageSize: 30,
      loadMoreSize: 20
    },
    // Item-specific colors for chips and UI elements
    itemColors: {
      disease:          'rgba(31, 119, 180, 0.75)',   // blue
      evidence:         'rgba(255, 127, 14, 0.75)',   // orange
      therapy:          'rgba(44, 160, 44, 0.75)',    // green
      assertion:        'rgba(214, 39, 40, 0.75)',    // red
      molecularprofile: 'rgba(148, 103, 189, 0.75)',  // purple
      variant:          'rgba(140, 86, 75, 0.75)',    // brown
      feature:          'rgba(227, 119, 194, 0.75)',  // pink
      source:           'rgba(127, 127, 127, 0.75)',  // gray
      phenotype:        'rgba(188, 189, 34, 0.75)',   // olive
      variantgroup:     'rgba(23, 190, 207, 0.75)',   // teal
      analysis:         'rgba(31, 119, 180, 0.75)',   // reuse blue or pick another
      recommendation:   'rgba(255, 127, 14, 0.75)',   // reuse orange
      treatmentplan:    'rgba(44, 160, 44, 0.75)'     // reuse green
    },
    
    
    
    
    
    // Glass effect styling
    glassEffect: {
      background: 'rgba(30, 30, 30, 0.6)',
      hoverBackground: 'rgba(40, 40, 40, 0.7)',
      borderOpacity: 0.7,
      hoverBorderOpacity: 1
    }
  }
};

/**
 * Helper function to get item details by ID
 * @param {string} id - The ID to look up
 * @returns {Object|null} - Item details or null if not found
 */
export function getItemById(id) {
  return oncoRiskConfig.items[id] || null;
}

/**
 * Get formatted groups with their items for sidebar rendering
 * @returns {Array} - Array of groups with items
 */
export function getSidebarGroups() {
  return oncoRiskConfig.groups.map(group => ({
    ...group,
    items: group.items.map(itemId => ({
      id: itemId,
      ...oncoRiskConfig.items[itemId]
    }))
  }));
}

/**
 * Get the base API URL
 * @returns {string} - API base URL
 */
export function getApiBaseUrl() {
  return oncoRiskConfig.api.baseUrl;
}

/**
 * Get full API endpoint URL
 * @param {string} endpoint - Endpoint key
 * @returns {string} - Full API endpoint URL
 */
export function getApiEndpoint(endpoint) {
  const baseUrl = oncoRiskConfig.api.baseUrl;
  const path = oncoRiskConfig.api.endpoints[endpoint];
  return `${baseUrl}${path}`;
}

/**
 * Get UI configuration
 * @returns {Object} - UI configuration
 */
export function getUiConfig() {
  return oncoRiskConfig.ui;
}

/**
 * Get color for a specific item type
 * @param {string} itemType - The item type to get color for
 * @returns {string} - Color value in rgba format
 */
// in oncorisk-config.js

export function getItemColor(itemType) {
  const defaultColor = 'rgba(165, 107, 74, 0.7)';
  if (!itemType) return defaultColor;

  let normalized = itemType.toLowerCase();

  // handle "ies" → "y" (e.g. therapies → therapy), then other trailing "s"
  if (normalized.endsWith('ies')) {
    normalized = normalized.slice(0, -3) + 'y';
  } else if (normalized.endsWith('s')) {
    normalized = normalized.slice(0, -1);
  }

  return oncoRiskConfig.ui.itemColors[normalized] || defaultColor;
}



export default oncoRiskConfig;
